import { adminFetch } from '../../auth/adminSession.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

export class ModelPerformanceApiError extends Error {
  constructor({ status, code, message }) {
    super(message || code || '모델 검증 정보를 불러오지 못했습니다.');
    this.name = 'ModelPerformanceApiError';
    this.status = status;
    this.code = code || (status === 401 ? 'AUTH_REQUIRED' : status === 403 ? 'ADMIN_PERMISSION_DENIED' : 'MODEL_PERFORMANCE_API_ERROR');
  }
}

function requiredString(value) { return typeof value === 'string' && value.trim() ? value : null; }
function requiredArray(value) { return Array.isArray(value) ? value : null; }
function normalizeAccess(body) {
  if (!body || typeof body !== 'object' || !Array.isArray(body.permissions)) throw new ModelPerformanceApiError({ code: 'ADMIN_ACCESS_UNAVAILABLE' });
  return body.permissions.filter((permission) => typeof permission === 'string');
}

function baseProjection(body) {
  if (!body || typeof body !== 'object' || Object.prototype.hasOwnProperty.call(body, 'segments')) {
    throw new ModelPerformanceApiError({ code: 'MODEL_PERFORMANCE_RESPONSE_INVALID' });
  }
  const artifactSha256 = requiredString(body.artifactSha256);
  const modelVersion = requiredString(body.modelVersion);
  const generatedAt = requiredString(body.generatedAt);
  const combinations = requiredArray(body.combinations);
  const calibrationBins = requiredArray(body.calibrationBins);
  if (!artifactSha256 || !modelVersion || !generatedAt || !combinations || !calibrationBins || !body.evaluation || typeof body.evaluation !== 'object') {
    throw new ModelPerformanceApiError({ code: 'MODEL_PERFORMANCE_RESPONSE_INVALID' });
  }
  return { artifactSha256, modelVersion, generatedAt, evaluation: body.evaluation, combinations, calibrationBins };
}

function diagnosticsProjection(body, base) {
  if (!body || typeof body !== 'object' || !Array.isArray(body.segments) || !body.segments.every((segment) => segment && typeof segment === 'object')
    || body.artifactSha256 !== base.artifactSha256 || body.modelVersion !== base.modelVersion || body.generatedAt !== base.generatedAt) {
    throw new ModelPerformanceApiError({ code: 'DIAGNOSTICS_SNAPSHOT_MISMATCH' });
  }
  return { segments: body.segments };
}

function runtimeProjection(body) {
  if (!body || body.status !== 'NORMAL' || !requiredString(body.modelVersion)
    || !/^[0-9a-f]{64}$/.test(body.artifactSha256 || '') || !requiredString(body.modelSource)
    || !requiredString(body.loadedAt)
    || JSON.stringify(body.supportedHorizons) !== JSON.stringify([60, 120, 180, 240])
    || JSON.stringify(body.supportedQuantities) !== JSON.stringify([1, 2, 3, 4, 5])) {
    throw new ModelPerformanceApiError({ code: 'MODEL_RUNTIME_RESPONSE_INVALID' });
  }
  return body;
}

async function request(path, signal) {
  try {
    const response = await adminFetch(`${API_BASE_URL}${path}`, { credentials: 'include', signal });
    let body = {};
    try { body = await response.json(); } catch (_) { /* body is optional on errors */ }
    if (!response.ok) throw new ModelPerformanceApiError({ status: response.status, code: body.code, message: body.message });
    return body;
  } catch (error) {
    if (error?.name === 'AbortError' || error instanceof ModelPerformanceApiError) throw error;
    throw new ModelPerformanceApiError({ status: error?.status, code: error?.code, message: error?.message });
  }
}

function runtimeResult(promise) {
  return promise.then((body) => ({ state: 'SUCCESS', data: runtimeProjection(body) })).catch((error) => {
    if (error?.name === 'AbortError') throw error;
    return { state: error?.status === 401 || error?.status === 403 ? 'FORBIDDEN' : 'ERROR', error };
  });
}

function diagnosticsResult(promise) {
  return promise.then((data) => ({ state: 'SUCCESS', data })).catch((error) => {
    if (error?.name === 'AbortError') throw error;
    return { state: error?.status === 401 || error?.status === 403 ? 'FORBIDDEN' : 'ERROR', error };
  });
}

export function createLiveModelPerformanceAdapter() {
  return {
    async load({ signal }) {
      const permissions = normalizeAccess(await request('/api/v1/admin/access', signal));
      const [base, runtime] = await Promise.all([
        request('/api/v1/admin/model-performance', signal).then(baseProjection),
        runtimeResult(request('/api/v1/admin/model-runtime', signal)),
      ]);
      const diagnostics = permissions.includes('MODEL_DIAGNOSTICS_READ')
        ? await diagnosticsResult(this.loadDiagnostics(base, { signal }))
        : { state: 'ACCESS_LIMITED', permission: 'MODEL_DIAGNOSTICS_READ' };
      return { base, runtime, diagnostics, permissions };
    },
    async loadBase({ signal }) {
      return baseProjection(await request('/api/v1/admin/model-performance', signal));
    },
    async loadDiagnostics(base, { signal }) {
      const artifactSha256 = encodeURIComponent(base.artifactSha256);
      return diagnosticsProjection(await request(`/api/v1/admin/model-performance/diagnostics?artifactSha256=${artifactSha256}`, signal), base);
    },
  };
}
